/**
 * Single source of truth for the public legal document set.
 *
 * Terms of Service governs use of the site and Service by anyone.
 * The Customer Agreement is the B2B contract layer that governs paid
 * subscriptions. The Privacy Policy governs personal data handling.
 *
 * Every legal page renders through `LegalDocumentLayout`, which uses this
 * registry for the cross-document footer and the print letterhead — so the
 * three documents always reference each other consistently.
 */
export type LegalDocSlug = 'customer-agreement' | 'terms' | 'privacy' | 'refund-policy';

export interface LegalDoc {
  slug: LegalDocSlug;
  path: string;
  /** Full document title, used as the page H1 and in the print letterhead. */
  title: string;
  /** Short label used in cross-links and navigation. */
  label: string;
  /** One-line purpose, shown in the cross-document footer. */
  summary: string;
  /** Meta description for search engines. */
  description: string;
  lastUpdated: string;
  /**
   * Operative version of the document. Bumped whenever the substantive text
   * changes, so recorded acceptances always point at exact wording.
   */
  version: string;
}

export const LEGAL_DOCS: Record<LegalDocSlug, LegalDoc> = {
  'customer-agreement': {
    slug: 'customer-agreement',
    path: '/customer-agreement',
    title: 'Customer Agreement',
    label: 'Customer Agreement',
    summary: 'The commercial contract that governs paid subscriptions and business accounts.',
    description:
      'The cannafax.ai Customer Agreement — subscription terms, confidentiality, data ownership, security and liability for business accounts.',
    lastUpdated: 'August 18, 2026',
    version: '2026-08-18',
  },
  terms: {
    slug: 'terms',
    path: '/terms',
    title: 'Terms of Service',
    label: 'Terms of Service',
    summary: 'The terms that apply to everyone who accesses or uses the Service.',
    description: 'Terms of Service for cannafax.ai cannabis inventory management platform.',
    lastUpdated: 'August 18, 2026',
    version: '2026-08-18',
  },
  privacy: {
    slug: 'privacy',
    path: '/privacy',
    title: 'Privacy Policy',
    label: 'Privacy Policy',
    summary: 'How we collect, use, share and protect personal information.',
    description: 'Privacy Policy for cannafax.ai cannabis inventory management platform.',
    lastUpdated: 'August 3, 2026',
    version: '2026-08-03',
  },
  'refund-policy': {
    slug: 'refund-policy',
    path: '/refund-policy',
    title: 'Refund Policy',
    label: 'Refund Policy',
    summary: 'Our cancellation terms and when we refund, in plain language.',
    description:
      'cannafax.ai refund and cancellation policy — cancel anytime, access continues to the end of your paid term, and refunds are granted at our discretion.',
    lastUpdated: 'August 18, 2026',
    version: '2026-08-18',
  },
};

export const LEGAL_DOC_ORDER: LegalDocSlug[] = [
  'customer-agreement',
  'terms',
  'privacy',
  'refund-policy',
];

export const SITE_ORIGIN = 'https://cannafax.ai';

/**
 * The document set a user accepts when they create an account, accept an
 * invitation, or purchase a subscription. Order is stable so the recorded
 * slug/version arrays line up.
 */
export const ACCEPTED_DOC_SLUGS: LegalDocSlug[] = ['customer-agreement', 'terms', 'privacy'];